import type {
    VorgangsMailUebernahmeDto,
    VorgangTerminDto,
} from "../../../services/calendarTerminService";

/**
 * Reine Helfer für den „Termine"-Reiter der Aktivitäts-Karte (SP12).
 * Bewusst ohne React/DOM, damit sie zeitzonen-robust unit-testbar sind.
 */

/** Start-Zeit eines Termins als ms seit Epoch; null/ungültig → NaN. */
export function terminStartMs(t: Pick<VorgangTerminDto, "start">): number {
    if (!t.start) return Number.NaN;
    return new Date(t.start).getTime();
}

/**
 * Sortiert Termine chronologisch absteigend (neueste/kommende zuerst). Termine
 * ohne bekannte Startzeit (kein Kalender-Lesezugriff aufs Postfach) wandern ans
 * Ende, dort stabil nach `terminId` absteigend.
 */
export function sortTermineByStart(list: VorgangTerminDto[]): VorgangTerminDto[] {
    return [...list].sort((a, b) => {
        const am = terminStartMs(a);
        const bm = terminStartMs(b);
        const aHas = !Number.isNaN(am);
        const bHas = !Number.isNaN(bm);
        if (aHas && bHas) return bm - am;
        if (aHas) return -1;
        if (bHas) return 1;
        return b.terminId - a.terminId;
    });
}

/**
 * Sprechendes Datum-Label eines Termins: „Di., 07.07.2026 · 14:30" bzw.
 * „Di., 07.07.2026 · ganztägig"; leer, wenn keine Startzeit bekannt ist.
 */
export function terminZeitLabel(t: Pick<VorgangTerminDto, "start" | "isAllDay">): string {
    if (!t.start) return "";
    const d = new Date(t.start);
    if (Number.isNaN(d.getTime())) return "";
    const datum = d.toLocaleDateString("de-DE", {
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
    if (t.isAllDay) return `${datum} · ganztägig`;
    const zeit = d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
    return `${datum} · ${zeit}`;
}

/** Zusammengesetzter, stabiler Schlüssel eines Termins (Postfach + Graph-Event). */
export function terminKey(t: Pick<VorgangTerminDto, "mailbox" | "graphEventId">): string {
    return `${t.mailbox}::${t.graphEventId}`;
}

/** Hängt der Termin DIREKT am Vorgang (nicht nur über eines seiner Todos)? */
export function istAmVorgang(
    t: Pick<VorgangTerminDto, "vorgangId">,
    vorgangId: number,
): boolean {
    return t.vorgangId === vorgangId;
}

/**
 * Altbestand-Erkennung (E06): der Termin hängt NUR über ein Todo am Vorgang.
 * Solche Zeilen bleiben sichtbar und lösbar, tragen aber keinen Todo-Bezug
 * mehr in der Oberfläche — die Verknüpfung wird nicht mehr angeboten.
 */
export function nurUeberTodo(
    t: Pick<VorgangTerminDto, "vorgangId" | "todoId">,
): boolean {
    return t.vorgangId == null && t.todoId != null;
}

/**
 * Teilt eine Terminliste an der Jetzt-Grenze. `kommend` steht aufsteigend
 * (nächster zuerst), `vergangen` absteigend (jüngster zuerst) — genau so
 * werden die beiden Blöcke gerendert. Termine ohne bekannte Startzeit (kein
 * Kalender-Lesezugriff) zählen als kommend, damit sie nicht in einem
 * eingeklappten Vergangenheits-Block verschwinden.
 */
export function teileNachJetzt<T extends { start?: string | null }>(
    liste: T[],
    jetzt: number = Date.now(),
): { kommend: T[]; vergangen: T[] } {
    const kommend: T[] = [];
    const vergangen: T[] = [];
    for (const t of liste) {
        const ms = t.start ? new Date(t.start).getTime() : Number.NaN;
        if (Number.isNaN(ms) || ms >= jetzt) kommend.push(t);
        else vergangen.push(t);
    }
    const ms = (t: T): number => (t.start ? new Date(t.start).getTime() : Number.NaN);
    kommend.sort((a, b) => {
        const am = ms(a);
        const bm = ms(b);
        if (Number.isNaN(am)) return 1;
        if (Number.isNaN(bm)) return -1;
        return am - bm;
    });
    vergangen.sort((a, b) => ms(b) - ms(a));
    return { kommend, vergangen };
}

/**
 * P9 — die Rückmeldung nach „alle Mails des Vorgangs an den Termin hängen",
 * als ein Satz.
 *
 * Warum überhaupt ein eigener Helfer: die Übernahme ist idempotent, und genau
 * das muss der Satz aushalten, ohne wie ein Fehlschlag zu klingen. Vier Fälle
 * treten wirklich auf — nichts zu tun (der Vorgang hat keine Mails), alles neu,
 * teils schon vorhanden (zweiter Lauf mit Nachzüglern), alles schon vorhanden
 * (reiner Wiederholungslauf) — plus die Kappung an der Server-Obergrenze, die
 * nie verschwiegen werden darf: sonst hielte man die Übernahme für vollständig.
 */
export function uebernahmeHinweis(e: VorgangsMailUebernahmeDto): string {
    const teile: string[] = [];
    if (e.angelegt === 0 && e.vorhanden === 0) {
        teile.push("Keine Mails am Vorgang, die übernommen werden könnten.");
    } else if (e.angelegt === 0) {
        teile.push(
            e.vorhanden === 1
                ? "Die Mail des Vorgangs hing bereits am Termin."
                : `Alle ${e.vorhanden} Mails des Vorgangs hingen bereits am Termin.`,
        );
    } else {
        teile.push(
            e.angelegt === 1
                ? "1 Mail des Vorgangs übernommen."
                : `${e.angelegt} Mails des Vorgangs übernommen.`,
        );
        if (e.vorhanden > 0) {
            teile.push(
                e.vorhanden === 1
                    ? "1 hing bereits am Termin."
                    : `${e.vorhanden} hingen bereits am Termin.`,
            );
        }
    }
    if (e.ausgelassen > 0) {
        teile.push(
            `${e.ausgelassen} ältere ${
                e.ausgelassen === 1 ? "Mail wurde" : "Mails wurden"
            } nicht übernommen — erneut ausführen, um sie nachzuholen.`,
        );
    }
    return teile.join(" ");
}
